import { useCallback, useEffect, useState } from 'react';
import { Activity, RefreshCw, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import DataTable, { type Column } from '../components/DataTable';
import Tabs from '../components/Tabs';
import Modal from '../components/Modal';
import StatusPill from '../components/StatusPill';
import FilterBar, {
  controlClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/FilterBar';
import { formatCurrency, formatDate } from '../lib/format';
import {
  fetchChain2PayPayments,
  fetchChain2PayPayouts,
  fetchChain2PayStatus,
  syncChain2PayPayments,
  reconcileChain2PayInvoice,
  type Chain2PayStatusResponse,
} from '../lib/api';

type Row = Record<string, unknown>;
type TabId = 'payments' | 'payouts';

function str(row: Row, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '' && typeof value !== 'object') {
      return String(value);
    }
  }
  return null;
}

function Chain2Pay() {
  const [tab, setTab] = useState<TabId>('payments');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState<Chain2PayStatusResponse | null>(null);
  const [reconcileFor, setReconcileFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const result =
      tab === 'payouts'
        ? await fetchChain2PayPayouts(50, 0)
        : await fetchChain2PayPayments('all', 50, 0);
    if (result.ok && result.data) setRows(result.data.data ?? []);
    else setRows([]);
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchChain2PayStatus().then((r) => {
      if (r.ok && r.data) setStatus(r.data);
    });
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    const result = await syncChain2PayPayments({});
    if (result.ok) {
      const s = String((result.data as Row)?.status ?? 'ok');
      if (s === 'ok') toast.success('Chain2Pay payments synced');
      else toast(s === 'not_configured' ? 'Chain2Pay is not configured' : `Sync: ${s}`);
      await load();
    }
    setSyncing(false);
  };

  const paymentColumns: Column<Row>[] = [
    { key: 'date', header: 'Date', render: (r) => formatDate(str(r, 'created_at', 'updated_at', 'date')) },
    { key: 'id', header: 'Payment ID', render: (r) => str(r, 'payment_id', 'id') ?? '—' },
    { key: 'description', header: 'Description', render: (r) => str(r, 'description', 'memo') ?? '—' },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) => formatCurrency(str(r, 'amount'), str(r, 'currency') ?? 'USD'),
    },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={str(r, 'status') ?? 'posted'} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => {
        const paymentId = str(r, 'payment_id', 'id');
        if (!paymentId) return null;
        return (
          <button type="button" onClick={() => setReconcileFor(paymentId)} className={secondaryButtonClass}>
            <Link2 size={14} /> Reconcile
          </button>
        );
      },
    },
  ];

  const payoutColumns: Column<Row>[] = [
    { key: 'date', header: 'Date', render: (r) => formatDate(str(r, 'created_at', 'date')) },
    { key: 'id', header: 'Payout ID', render: (r) => str(r, 'payout_id', 'id') ?? '—' },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) => formatCurrency(str(r, 'amount'), str(r, 'currency') ?? 'USD'),
    },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={str(r, 'status') ?? 'posted'} /> },
  ];

  return (
    <PageShell
      title="Chain2Pay"
      subtitle="Crypto payment gateway payments, payouts, and reconciliation."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Connection"
          value={status ? (status.connected ? 'Connected' : status.configured ? 'Configured' : 'Not configured') : '…'}
          tone={status?.connected ? 'success' : status?.configured ? 'warning' : 'neutral'}
          icon={<Activity size={18} />}
        />
        <StatCard label="Records" value={loading ? '…' : String(rows.length)} tone="info" />
        <StatCard
          label="Last error"
          value={status?.last_error ? 'See details' : 'None'}
          tone={status?.last_error ? 'danger' : 'success'}
        />
        <StatCard label="Gateway" value="Chain2Pay" tone="neutral" />
      </div>

      <Tabs
        tabs={[
          { id: 'payments', label: 'Payments' },
          { id: 'payouts', label: 'Payouts' },
        ]}
        active={tab}
        onChange={(id) => setTab(id as TabId)}
      />

      <FilterBar
        actions={
          <button type="button" onClick={handleSync} disabled={syncing} className={primaryButtonClass}>
            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} /> Sync payments
          </button>
        }
      >
        <span className="text-sm text-zt-muted">
          {tab === 'payments' ? 'Gateway payments' : 'Gateway payouts'}
        </span>
      </FilterBar>

      <DataTable
        columns={tab === 'payouts' ? payoutColumns : paymentColumns}
        rows={rows}
        rowKey={(r, i) => str(r, 'payment_id', 'payout_id', 'id') ?? String(i)}
        loading={loading}
        emptyTitle={tab === 'payouts' ? 'No payouts' : 'No payments'}
        emptyMessage="Connect Chain2Pay and sync to see gateway activity."
      />

      <ReconcileModal
        paymentId={reconcileFor}
        onClose={() => setReconcileFor(null)}
        onDone={load}
      />
    </PageShell>
  );
}

function ReconcileModal({
  paymentId,
  onClose,
  onDone,
}: {
  paymentId: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [invoiceId, setInvoiceId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!paymentId || !invoiceId.trim()) {
      toast.error('Invoice ID is required');
      return;
    }
    setSubmitting(true);
    const result = await reconcileChain2PayInvoice({
      payment_id: paymentId,
      invoice_id: invoiceId.trim(),
    });
    setSubmitting(false);
    if (result.ok) {
      toast.success('Payment reconciled');
      setInvoiceId('');
      onClose();
      onDone();
    }
  };

  return (
    <Modal
      open={paymentId !== null}
      onClose={onClose}
      title="Reconcile payment"
      description={paymentId ? `Match payment ${paymentId} to an invoice.` : undefined}
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className={primaryButtonClass}>
            {submitting ? 'Reconciling…' : 'Reconcile'}
          </button>
        </>
      }
    >
      <label className="mb-1 block text-sm font-medium text-slate-200">Invoice ID</label>
      <input
        type="text"
        value={invoiceId}
        onChange={(e) => setInvoiceId(e.target.value)}
        placeholder="INV-0001"
        className={`w-full ${controlClass}`}
      />
    </Modal>
  );
}

export default Chain2Pay;
